import React from 'react';
import PropTypes from 'prop-types';
import Prism from 'prismjs';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-typescript';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-java';
import 'prismjs/components/prism-kotlin';
import 'prismjs/components/prism-sql';
import 'prismjs/components/prism-go';

const CODE_FENCE = /```([\w#+.-]*)[^\S\r\n]*(?:\r?\n)?([\s\S]*?)```/g;

const LANGUAGE_ALIASES = {
    js: 'javascript',
    ts: 'typescript',
    py: 'python',
    golang: 'go',
};

function normalizeLanguage(language) {
    const normalized = String(language || '').trim().toLowerCase();
    return LANGUAGE_ALIASES[normalized] ?? normalized;
}

export function parsePracticeContent(text, fallbackLanguage = '') {
    const value = String(text ?? '');
    const parts = [];
    let lastIndex = 0;
    let match;

    CODE_FENCE.lastIndex = 0;
    while ((match = CODE_FENCE.exec(value)) !== null) {
        if (match.index > lastIndex) {
            parts.push({type: 'text', value: value.slice(lastIndex, match.index)});
        }
        parts.push({
            type: 'code',
            language: normalizeLanguage(match[1] || fallbackLanguage) || 'text',
            value: match[2].replace(/^\r?\n/, '').replace(/\r?\n$/, ''),
        });
        lastIndex = CODE_FENCE.lastIndex;
    }

    if (lastIndex < value.length) {
        parts.push({type: 'text', value: value.slice(lastIndex)});
    }

    return parts.some((part) => part.type === 'code')
        ? parts.filter((part) => part.type === 'code' || part.value.trim())
        : [{type: 'text', value}];
}

function HighlightedCode({code, language}) {
    const grammar = Prism.languages[language] ?? Prism.languages.clike ?? Prism.languages.javascript;
    const highlighted = Prism.highlight(code, grammar, language);

    return (
        <div className="overflow-hidden rounded-xl border border-[#31384C] bg-[#0f1117]">
            <div className="border-b border-[#31384C] px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-gray-500">
                {language}
            </div>
            <pre className="m-0 overflow-x-auto bg-[#0f1117] p-4 text-sm leading-relaxed">
                <code dangerouslySetInnerHTML={{__html: highlighted}} />
            </pre>
        </div>
    );
}

HighlightedCode.propTypes = {
    code: PropTypes.string.isRequired,
    language: PropTypes.string.isRequired,
};

export default function PracticeContent({text, language = '', textClassName = ''}) {
    const parts = parsePracticeContent(text, language);
    const containsCode = parts.some((part) => part.type === 'code');

    if (!containsCode) {
        return <div className={`whitespace-pre-wrap ${textClassName}`}>{parts[0]?.value}</div>;
    }

    return (
        <div className="space-y-3">
            {parts.map((part, index) => part.type === 'code' ? (
                <HighlightedCode key={`code-${index}`} code={part.value} language={part.language} />
            ) : (
                <div key={`text-${index}`} className={`whitespace-pre-wrap ${textClassName}`}>{part.value.trim()}</div>
            ))}
        </div>
    );
}

PracticeContent.propTypes = {
    text: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    language: PropTypes.string,
    textClassName: PropTypes.string,
};
